import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import {
  Language,
  getDirection,
  getFontClass,
  translations,
} from '../thi/thi-translations';
import { normalizeThiLang } from '../thi/thi-lang.util';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  InvoiceSubscriptionPlan,
  InvoiceSubscriptionsV2Service,
  PublicCompanyExtensionPlanItem,
  PublicDocExtensionPlanItem,
  PublicSubscriptionPlanItem,
  PublicTokenExtensionPlanItem,
} from 'src/app/services/invoice-subscriptions-v2.service';

/** Precomputed row for the comparison table */
interface ComparisonMetricRow {
  label: string;
  cells: string[];
}

@Component({
  selector: 'app-sub-plans',
  templateUrl: './sub-plans.component.html',
  styleUrls: ['./sub-plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubPlansComponent implements OnInit {
  landingLang: Language = 'ar';
  dir: 'rtl' | 'ltr' = 'rtl';
  fontClass = 'font-cairo';
  t = translations.ar;

  readonly registerLink = '/register';

  plans: InvoiceSubscriptionPlan[] = [];
  docExtensionPlans: PublicDocExtensionPlanItem[] = [];
  companyExtensionPlans: PublicCompanyExtensionPlanItem[] = [];
  tokenExtensionPlans: PublicTokenExtensionPlanItem[] = [];

  plansLoading = true;
  plansError = false;

  /** Column index highlighted as “popular” (middle tier when multiple plans). */
  popularPlanIndex = 0;

  comparisonMetricRows: ComparisonMetricRow[] = [];

  constructor(
    private readonly invoiceSubsV2: InvoiceSubscriptionsV2Service,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get arrowIcon(): string {
    return this.dir === 'rtl' ? 'fa fa-arrow-left' : 'fa fa-arrow-right';
  }

  ngOnInit(): void {
    const raw = localStorage.getItem('curentLang') || 'ar';
    this.landingLang = normalizeThiLang(raw);
    this.dir = getDirection(this.landingLang);
    this.fontClass = getFontClass(this.landingLang);
    this.t = translations[this.landingLang];

    this.fetchPlans(false);
  }

  reloadPlans(): void {
    this.fetchPlans(true);
  }

  private fetchPlans(resetLoading: boolean): void {
    if (resetLoading) {
      this.plansLoading = true;
      this.plansError = false;
      this.cdr.markForCheck();
    }

    const emptyAddons = <T>() =>
      of({ status: 200 as const, count: 0, plans: [] as T[] });

    forkJoin({
      subscription: this.invoiceSubsV2.getPublicSubscriptionPlans(),
      docExt: this.invoiceSubsV2
        .getPublicDocExtensionPlans()
        .pipe(catchError(() => emptyAddons<PublicDocExtensionPlanItem>())),
      companyExt: this.invoiceSubsV2
        .getPublicCompanyExtensionPlans()
        .pipe(catchError(() => emptyAddons<PublicCompanyExtensionPlanItem>())),
      tokenExt: this.invoiceSubsV2
        .getPublicTokenExtensionPlans()
        .pipe(catchError(() => emptyAddons<PublicTokenExtensionPlanItem>())),
    }).subscribe({
      next: ({ subscription, docExt, companyExt, tokenExt }) => {
        const mapped = (subscription.plans ?? []).map((p) =>
          this.mapPublicSubscriptionPlan(p),
        );
        this.plans = this.normalizePlans(mapped);
        this.popularPlanIndex = this.computePopularIndex(this.plans);
        this.comparisonMetricRows = this.buildComparisonRows(this.plans);
        this.docExtensionPlans = docExt.plans ?? [];
        this.companyExtensionPlans = companyExt.plans ?? [];
        this.tokenExtensionPlans = tokenExt.plans ?? [];
        this.plansLoading = false;
        this.plansError = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.plans = [];
        this.docExtensionPlans = [];
        this.companyExtensionPlans = [];
        this.tokenExtensionPlans = [];
        this.comparisonMetricRows = [];
        this.plansLoading = false;
        this.plansError = true;
        this.cdr.markForCheck();
      },
    });
  }

  private mapPublicSubscriptionPlan(
    p: PublicSubscriptionPlanItem,
  ): InvoiceSubscriptionPlan {
    return {
      id: p.id,
      name_ar: p.name_ar ?? '',
      name_en: p.name_en ?? '',
      name_he: p.name_he ?? '',
      description_ar: p.description_ar,
      description_en: p.description_en,
      description_he: p.description_he,
      documents_limit: p.documents_limit,
      companies_limit: p.companies_limit,
      duration_days: p.duration_days,
      price: p.price,
      is_active: true,
      analysis_tokens_limit: p.analysis_tokens_limit,
      sort_order: p.id,
    };
  }

  planName(p: InvoiceSubscriptionPlan): string {
    if (this.landingLang === 'ar') {
      return p.name_ar || p.name_en || p.name_he || '';
    }
    if (this.landingLang === 'he') {
      return p.name_he || p.name_en || p.name_ar || '';
    }
    return p.name_en || p.name_ar || p.name_he || '';
  }

  planDesc(p: InvoiceSubscriptionPlan): string {
    if (this.landingLang === 'ar') {
      return p.description_ar || p.description_en || p.description_he || '';
    }
    if (this.landingLang === 'he') {
      return p.description_he || p.description_en || p.description_ar || '';
    }
    return p.description_en || p.description_ar || p.description_he || '';
  }

  /** Bullet lines under each card — comma-separated description from API */
  planFeatureLines(plan: InvoiceSubscriptionPlan): string[] {
    const d = this.planDesc(plan);
    if (!d.trim()) {
      return [];
    }
    return d
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  formatPrice(plan: InvoiceSubscriptionPlan): string {
    const n = this.numericPrice(plan.price);
    const formatted =
      Number.isFinite(n)
        ? n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '';
    const suffix = this.pricePeriodSuffix(plan.duration_days);
    return suffix ? `${formatted}${suffix}` : formatted;
  }

  isPopular(index: number): boolean {
    return index === this.popularPlanIndex && this.plans.length > 0;
  }

  trackByPlanId(_i: number, p: InvoiceSubscriptionPlan): number {
    return p.id;
  }

  trackByAddonId(_i: number, p: { id: number }): number {
    return p.id;
  }

  addonDisplayName(p: {
    name_ar?: string;
    name_en?: string;
    name_he?: string;
  }): string {
    if (this.landingLang === 'ar') {
      return p.name_ar || p.name_en || p.name_he || '';
    }
    if (this.landingLang === 'he') {
      return p.name_he || p.name_en || p.name_ar || '';
    }
    return p.name_en || p.name_ar || p.name_he || '';
  }

  addonDesc(p: {
    description_ar?: string;
    description_en?: string;
    description_he?: string;
  }): string {
    if (this.landingLang === 'ar') {
      return p.description_ar || p.description_en || p.description_he || '';
    }
    if (this.landingLang === 'he') {
      return p.description_he || p.description_en || p.description_ar || '';
    }
    return p.description_en || p.description_ar || p.description_he || '';
  }

  formatAddonPrice(price: number): string {
    const n = Number(price);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '—';
  }

  docPackBadge(extra: number): string {
    const L = this.landingLang;
    if (L === 'ar') {
      return `+${extra} مستندات`;
    }
    if (L === 'he') {
      return `+${extra} מסמכים`;
    }
    return `+${extra} documents`;
  }

  companyPackBadge(extra: number): string {
    const L = this.landingLang;
    if (L === 'ar') {
      return `+${extra} شركات`;
    }
    if (L === 'he') {
      return `+${extra} חברות`;
    }
    return `+${extra} companies`;
  }

  tokenPackBadge(plan: PublicTokenExtensionPlanItem): string {
    const n =
      plan.extra_tokens ?? plan.documents_count ?? null;
    if (n == null) {
      return '—';
    }
    const L = this.landingLang;
    if (L === 'ar') {
      return `+${n} رموز`;
    }
    if (L === 'he') {
      return `+${n} טוקנים`;
    }
    return `+${n} tokens`;
  }

  private numericPrice(price: number | string): number {
    if (typeof price === 'number') {
      return price;
    }
    const n = parseFloat(String(price).replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }

  private pricePeriodSuffix(durationDays: number): string {
    const L = this.landingLang;
    const ar = L === 'ar';
    const he = L === 'he';
    if (durationDays >= 365 && durationDays % 365 === 0) {
      const years = durationDays / 365;
      if (years <= 1) {
        return ar ? '/سنة' : he ? '/שנה' : '/year';
      }
      return ar ? `/${years} سنوات` : he ? `/${years} שנים` : `/${years} yrs`;
    }
    if (durationDays >= 30 && durationDays % 30 === 0) {
      const months = durationDays / 30;
      return ar ? `/${months} شهر` : he ? `/${months} חודשים` : `/${months} mo`;
    }
    return ar ? `/${durationDays} يوم` : he ? `/${durationDays} ימים` : `/${durationDays} d`;
  }

  private formatDurationDays(days: number): string {
    const L = this.landingLang;
    const ar = L === 'ar';
    const he = L === 'he';
    if (days >= 365 && days % 365 === 0) {
      const y = days / 365;
      if (y <= 1) {
        return ar ? 'سنة واحدة' : he ? 'שנה אחת' : '1 year';
      }
      return ar ? `${y} سنوات` : he ? `${y} שנים` : `${y} years`;
    }
    if (days >= 30 && days % 30 === 0) {
      const m = days / 30;
      return ar ? `${m} شهرًا` : he ? `${m} חודשים` : `${m} months`;
    }
    return String(days);
  }

  private normalizePlans(rows: InvoiceSubscriptionPlan[]): InvoiceSubscriptionPlan[] {
    const list = (rows ?? []).filter((p) => p.is_active !== false);
    list.sort((a, b) => {
      const sa = a.sort_order ?? a.id;
      const sb = b.sort_order ?? b.id;
      return sa - sb;
    });
    return list;
  }

  private computePopularIndex(plans: InvoiceSubscriptionPlan[]): number {
    const n = plans.length;
    if (n <= 0) {
      return 0;
    }
    return Math.min(n - 1, Math.max(0, Math.floor(n / 2)));
  }

  private buildComparisonRows(plans: InvoiceSubscriptionPlan[]): ComparisonMetricRow[] {
    const L = this.landingLang;
    const ar = L === 'ar';
    const he = L === 'he';

    const docLabel = ar ? 'مستندات الفاتورة' : he ? 'מסמכי חשבונית' : 'Invoice documents';
    const coLabel = ar ? 'الشركات' : he ? 'חברות' : 'Companies';
    const tokLabel = ar ? 'رموز التحليل' : he ? 'טוקני ניתוח' : 'Analysis tokens';

    const rows: ComparisonMetricRow[] = [
      {
        label: docLabel,
        cells: plans.map((p) => String(p.documents_limit ?? '—')),
      },
      {
        label: coLabel,
        cells: plans.map((p) => String(p.companies_limit ?? '—')),
      },
      {
        label: tokLabel,
        cells: plans.map((p) =>
          p.analysis_tokens_limit != null ? String(p.analysis_tokens_limit) : '—',
        ),
      },
      {
        label: ar ? 'المدة' : he ? 'משך' : 'Duration',
        cells: plans.map((p) => this.formatDurationDays(p.duration_days)),
      },
      {
        label: ar ? 'السعر' : he ? 'מחיר' : 'Price',
        cells: plans.map((p) => {
          const n = this.numericPrice(p.price);
          return Number.isFinite(n)
            ? n.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '—';
        }),
      },
    ];

    return rows;
  }
}
